import { subYears } from "date-fns";
import * as Yup from "yup";
import { Helper } from "../../utils/Helpers";
import { SUPPORTED_IMAGE_FORMATS } from "../../utils/Constants";
import { todayMinus18Years } from "../../utils/Constants";


const validateFileOrUrl = (value) => {

  if (!value) return true;
  if (typeof value === "string") return true;
  const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];
  return value && SUPPORTED_FORMATS.includes(value.type);
};

export const EmployeePersonalInfoSchema = Yup.object().shape({
  employee_name: Yup.string()
    .required("Please fill in the employee name")
    .trim()
    .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
    .min(3, "Employee name should have at least 3 characters")
    .max(60, "Employee name should not exceed 60 characters"),

  mobile: Yup.string()
    .required("Mobile number is required")
    .min(7, "Please enter a valid number")
    .matches(/^[0-9]+$/, "Must be only digits")
    .max(15, "Please enter a valid number")
    .test("is-valid-mobile", "Please enter a valid number", function (value) {
      const { nationality } = this.parent;
      if (nationality === "indian") {
        return /^[1-9][0-9]{9}$/.test(value);
      }
      return true;
    }),

  alternative_mobile: Yup.string()
    .optional()
    .min(7, "Please enter a valid number")
    .matches(/^[0-9]+$/, "Must be only digits")
    .max(15, "Please enter a valid number")
    .nullable(),

  father_name: Yup.string()
    .required("Please fill in the father's name")
    .trim()
    .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
    .min(3, "Father's name should have at least 3 characters")
    .max(60, "Father's name should not exceed 60 characters"),

  dob: Yup.date()
    .transform((value, originalValue) =>
      originalValue === "" ? null : new Date(originalValue)
    )
    .typeError("Date of birth must be a valid date")
    .max(todayMinus18Years, "Employee must be at least 18 years old")
    .required("Date of birth is required"),

  state: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  country: Yup.string().required("Country is required"),

  email: Yup.string()
    .trim()
    .email("Please enter a valid email address")
    .nullable()
    .optional(),

  gender: Yup.string().required("Gender is required"),
  marital_status_id: Yup.string().trim().required("Marital status is required"),

  permanent_address: Yup.string()
    .required("Permanent address is required")
    .trim()
    .min(5, "Address should have at least 5 characters")
    .max(250, "Address should not exceed 250 characters"),

  present_address: Yup.string()
    .required("Present address is required")
    .trim()
    .min(5, "Address should have at least 5 characters")
    .max(250, "Address should not exceed 250 characters"),

  proof_type_id: Yup.string().required("Proof type is required"),

  proof_number: Yup.string()
    .required("Proof number is required")
    .test("proof-validation", "Invalid proof number format", function (value) {
      const proofLabel = this.parent.proof_type_id;
      if (!value || !proofLabel) return true;

      const type = proofLabel.toLowerCase();

      if (type.includes("aadhaar") || type.includes("aadhar")) {
        return /^[2-9]{1}[0-9]{11}$/.test(value)
          ? true
          : this.createError({ message: "Invalid Aadhaar (12 Digits, starts with 2-9)" });
      }
      if (type.includes("pan")) {
        return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)
          ? true
          : this.createError({ message: "Invalid PAN Format (e.g. ABCDE1234F)" });
      }
      if (type.includes("driving") || type.includes("license")) {
        return /^[A-Z0-9\-\s]{10,20}$/.test(value)
          ? true
          : this.createError({ message: "Invalid Driving License Number" });
      }
      if (type.includes("voter") || type.includes("epic")) {
        return /^[A-Z0-9]{10}$/.test(value)
          ? true
          : this.createError({ message: "Invalid Voter ID (e.g. ABC1234567)" });
      }
      if (type.includes("passport")) {
        return /^[A-Z]{1}[0-9]{7}$/.test(value)
          ? true
          : this.createError({ message: "Invalid Passport Number" });
      }
      return value.length >= 5;
    }),

  pan_no: Yup.string()
    .required("PAN number is required")
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Please enter a valid PAN number"),

  designation_id: Yup.string().required("Designation is required"),
  department_id: Yup.string().required("Department is required"),
  employment_type_id: Yup.string().required("Employment type is required"),

  date_of_joining: Yup.date()
    .transform((value, originalValue) =>
      originalValue === "" ? null : new Date(originalValue)
    )
    .typeError("Date of joining must be a valid date")
    .required("Date of joining is required")
    .max(new Date(), "Date of joining cannot be in the future"),

  employee_photo: Yup.mixed()
    .required("Employee photo is required")
    .test("fileFormat", "Only JPEG, JPG, and PNG formats are allowed", validateFileOrUrl)
    .test("fileSize", "File size must be under 5MB", (value) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return value.size <= 5 * 1024 * 1024;
    }),

  id_proof_front: Yup.mixed()
    .required("Front proof photo is required")
    .test("fileFormat", "Only JPEG, JPG, and PNG formats are allowed", validateFileOrUrl)
    .test("fileSize", "File size must be under 5MB", (value) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return value.size <= 5 * 1024 * 1024;
    }),

  id_proof_back: Yup.mixed()
    .required("Back proof photo is required")
    .test("fileFormat", "Only JPEG, JPG, and PNG formats are allowed", validateFileOrUrl)
    .test("fileSize", "File size must be under 5MB", (value) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return value.size <= 5 * 1024 * 1024;
    }),
});

export const EmployeeEducationAndProfessionalInfoSchema = Yup.object().shape({
  educations: Yup.array()
    .of(
      Yup.object().shape({
        education_level_id: Yup.string().required(
          "Education level is required"
        ),
        institute_name: Yup.string()
          .required("Institution name is required")
          .trim()
          .matches(
            /^[A-Za-z\s]+$/,
            "Institution name must contain only alphabets"
          )
          .min(2, "Institution name should be at least 2 characters long"),

        start_date: Yup.date()
          .required("Start date is required")
          .max(new Date(), "Start date cannot be in the future"),

        end_date: Yup.date()
          .required("End date is required")
          .min(Yup.ref("start_date"), "End date must be after start date")
          .max(new Date(), "End date cannot be in the future"),
      })
    )
    .min(0),

  professionals: Yup.array()
    .of(
      Yup.object().shape({
        company_name: Yup.string()
          .required("Company name is required")
          .trim()
          .matches(/^[A-Za-z\s]+$/, "Company name must contain only alphabets")
          .min(2, "Company name should be at least 2 characters long"),

        designation: Yup.string()
          .required("Designation is required")
          .trim()
          .matches(/^[A-Za-z\s]+$/, "Designation must contain only alphabets")
          .min(2, "Designation should be at least 2 characters long"),

        ctc: Yup.number()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .nullable()
          .required("CTC is required")
          .min(0, "CTC cannot be negative")
          .max(100000000, "CTC must be reasonable")
          .typeError("CTC must be a valid number"),

        start_date: Yup.date()
          .required("Start date is required")
          .max(new Date(), "Start date cannot be in the future"),

        end_date: Yup.date()
          .required("End date is required")
          .min(Yup.ref("start_date"), "End date must be after start date")
          .max(new Date(), "End date cannot be in the future"),
      })
    )
    .min(0),
});

export const EmployeeBankInfoSchema = Yup.object().shape({
  bank_name: Yup.string()
    .required("Bank name is required")
    .trim()
    .min(2, "Bank name should be at least 2 characters long")
    .max(100, "Bank name should not exceed 100 characters"),
  ifsc_code: Yup.string()
    .required("IFSC code is required")
    .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Please enter a valid IFSC code (e.g., SBIN0001234)")
    .trim()
    .uppercase(),
  account_holder_name: Yup.string()
    .required("Account holder name is required")
    .trim()
    .min(3, "Account holder name should be at least 3 characters long")
    .max(100, "Account holder name should not exceed 100 characters")
    .matches(/^[a-zA-Z\s]+$/, "Account holder name should contain only letters and spaces"),
  account_no: Yup.string()
    .required("Account number is required")
    .matches(
      /^[0-9]{9,18}$/,
      "Please enter a valid account number (9-18 digits)"
    ),

  branch: Yup.string()
    .required("Branch is required")
    .trim()
    .min(2, "Branch name should be at least 2 characters long")
    .max(100, "Branch name should not exceed 100 characters"),
});

export const EmployeeSalaryInfoSchema = Yup.object().shape({
  basic: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .required("Basic pay is required")
    .min(0, "Basic pay cannot be negative")
    .max(10000000, "Basic pay must be reasonable")
    .typeError("Basic pay must be a valid number"),
  hra: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .required("HRA is required")
    .min(0, "HRA cannot be negative")
    .max(5000000, "HRA must be reasonable")
    .typeError("HRA must be a valid number"),
  other_allowances: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .required("Other allowances are required")
    .min(0, "Other allowances cannot be negative")
    .max(5000000, "Other allowances must be reasonable")
    .typeError("Other allowances must be a valid number"),
  employee_pf: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .required("Employee PF is required")
    .min(0, "Employee PF cannot be negative")
    .max(1000000, "Employee PF must be reasonable")
    .typeError("Employee PF must be a valid number")
    .test("pf-limit", "Employee PF cannot exceed 12% of basic pay", function (value) {
      const { basic } = this.parent;
      if (basic && value) {
        return value <= (basic * 0.12);
      }
      return true;
    }),
  professional_tax: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .required("Professional tax is required")
    .min(0, "Professional tax cannot be negative")
    .max(2500, "Professional tax cannot exceed ₹2500 per month")
    .typeError("Professional tax must be a valid number"),
  employer_pf: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .required("Employer PF is required")
    .min(0, "Employer PF cannot be negative")
    .max(1000000, "Employer PF must be reasonable")
    .typeError("Employer PF must be a valid number")
    .test("employer-pf-limit", "Employer PF cannot exceed 12% of basic pay", function (value) {
      const { basic } = this.parent;
      if (basic && value) {
        return value <= (basic * 0.12);
      }
      return true;
    }),
});